import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Camera, MessageCircle, Lock, Calendar, BarChart2, Megaphone, Sparkles, Wrench, CheckCircle, MapPin, Shield, TrendingUp, Search, Star, Clock, Globe, FileText, Wallet, Bell, Eye, Share2, CreditCard } from "lucide-react";
import EnrichmentModal from "@/pages/manchester/EnrichmentModal";
import { FEATURE_PAGES } from "./featureData";

const ICONS = { Home, Camera, MessageCircle, Lock, Calendar, BarChart2, Megaphone, Sparkles, Wrench, CheckCircle, MapPin, Shield, TrendingUp, Search, Star, Clock, Globe, FileText, Wallet, Bell, Eye, Share2, CreditCard };

export default function FeaturePage({ slug }) {
  const data = FEATURE_PAGES[slug];
  const [showModal, setShowModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(1);

  useEffect(() => {
    if (!data) return;
    document.title = data.metaTitle;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", data.metaDescription);
    setMeta("robots", "index, follow");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = window.location.origin + "/management/" + slug;
    window.scrollTo(0, 0);
  }, [slug]);

  if (!data) return null;

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>

      {/* Hero — full background image with dark overlay */}
      <section style={{
        position: "relative",
        paddingTop: "clamp(130px, 15vw, 180px)",
        paddingBottom: "clamp(60px, 7vw, 80px)",
        paddingLeft: "clamp(20px, 5vw, 80px)",
        paddingRight: "clamp(20px, 5vw, 80px)",
        overflow: "hidden",
      }}>
        <img src={data.heroImage} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0 }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(0,0,0,0.82), rgba(0,0,0,0.65))", zIndex: 1 }} />
        <div style={{ position: "relative", zIndex: 2, maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ maxWidth: 640 }}>
            <div style={{ display: "inline-flex", alignItems: "center", background: "rgba(204,245,172,0.12)", border: "1px solid rgba(204,245,172,0.3)", borderRadius: 100, padding: "6px 14px", marginBottom: 24 }}>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: "#CCF5AC", letterSpacing: "0.06em", textTransform: "uppercase" }}>{data.category}</span>
            </div>
            <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "clamp(38px, 5vw, 60px)", lineHeight: 1.1, letterSpacing: "-0.025em", color: "#F4F1E7", margin: "0 0 24px" }}>{data.title}</h1>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "clamp(16px, 2vw, 19px)", color: "rgba(244,241,231,0.85)", lineHeight: 1.65, margin: "0 0 32px", maxWidth: 480 }}>{data.heroSubtitle}</p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }} className="feature-hero-btns">
              <button
                onClick={() => setShowModal(true)}
                style={{ background: "#CD533B", color: "#fff", border: "none", borderRadius: 10, padding: "15px 28px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#B8462F"}
                onMouseLeave={e => e.currentTarget.style.background = "#CD533B"}
              >
                Get Free Valuation <ArrowRight size={16} />
              </button>
              <Link
                to="/locations"
                style={{ background: "transparent", border: "1.5px solid rgba(244,241,231,0.4)", color: "#F4F1E7", borderRadius: 10, padding: "14px 26px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 500, textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "border-color 0.2s, background 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#F4F1E7"; e.currentTarget.style.background = "rgba(244,241,231,0.08)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(244,241,231,0.4)"; e.currentTarget.style.background = "transparent"; }}
              >
                Explore locations
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section style={{ backgroundColor: "#F4F1E7", padding: "clamp(48px, 6vw, 64px) clamp(20px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24 }} className="feature-stats-grid">
            {data.stats.map(({ val, label }) => (
              <div key={label} style={{ textAlign: "center" }}>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: "clamp(28px, 3vw, 36px)", color: "#112215", margin: 0, lineHeight: 1.1 }}>{val}</p>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#5C7268", margin: "8px 0 0" }}>{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story — 2 col */}
      <section style={{ backgroundColor: "#fff", padding: "clamp(60px, 7vw, 96px) clamp(20px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }} className="feature-story">
            <div>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#CD533B", marginBottom: 12, marginTop: 0 }}>{data.storyLabel}</p>
              <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(32px, 4vw, 44px)", color: "#112215", margin: "0 0 20px", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
                {data.storyTitle}
              </h2>
              {data.storyBody.map((p, i) => (
                <p key={i} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#5C7268", lineHeight: 1.75, marginBottom: 16, marginTop: 0 }}>
                  {p}
                </p>
              ))}
              <button
                onClick={() => setShowModal(true)}
                style={{ background: "#CD533B", color: "#fff", borderRadius: 8, padding: "14px 28px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, transition: "background 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#B8462F"}
                onMouseLeave={e => e.currentTarget.style.background = "#CD533B"}
              >
                Get Free Valuation <ArrowRight size={15} />
              </button>
            </div>
            <div style={{ borderRadius: 20, overflow: "hidden", border: "none" }}>
              <img src={data.storyImage} alt={data.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", borderRadius: 20 }} />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits — interactive grid */}
      <section style={{ backgroundColor: "#fff", padding: "clamp(60px, 7vw, 96px) clamp(20px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5C7268", marginBottom: 12, marginTop: 0 }}>What's included</p>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em", lineHeight: 1.15, color: "#112215", margin: "0 auto 14px", maxWidth: 640 }}>
              Everything we handle for you.
            </h2>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#5C7268", lineHeight: 1.6, maxWidth: 520, margin: "0 auto" }}>
              A complete {data.title.toLowerCase()} service, delivered by a team that cares about your income as much as you do.
            </p>
          </div>

          <div className="feature-benefits-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
            {data.benefits.map(({ icon, title, desc }, i) => {
              const isActive = activeIndex === i;
              const Icon = ICONS[icon] || Star;
              return (
                <div
                  key={title}
                  onMouseEnter={() => setActiveIndex(i)}
                  style={{
                    background: isActive ? "#112215" : "#F4F1E7",
                    borderRadius: 14,
                    padding: "26px 26px 30px",
                    display: "flex",
                    flexDirection: "column",
                    transform: isActive ? "rotate(-1.5deg) scale(1.03)" : "rotate(0deg) scale(1)",
                    boxShadow: isActive ? "0 20px 50px rgba(0,0,0,0.22)" : "0 0 0 rgba(0,0,0,0)",
                    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    zIndex: isActive ? 2 : 1,
                    position: "relative",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 16, color: isActive ? "#F4F1E7" : "#112215", letterSpacing: "-0.01em", transition: "color 0.25s" }}>{title}</span>
                    <div style={{ width: 34, height: 34, borderRadius: 9, background: isActive ? "rgba(255,255,255,0.15)" : "#E8E4D5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.25s" }}>
                      <Icon size={16} color={isActive ? "#F4F1E7" : "#5C7268"} strokeWidth={1.8} />
                    </div>
                  </div>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13.5, color: isActive ? "rgba(244,241,231,0.75)" : "#5C7268", lineHeight: 1.65, margin: 0, transition: "color 0.25s" }}>{desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {showModal && (
        <EnrichmentModal mode="full" baseForm={{}} onClose={() => setShowModal(false)} onSubmitted={() => setShowModal(false)} />
      )}

      <style>{`
        @media (max-width: 900px) {
          .feature-story { grid-template-columns: 1fr !important; gap: 48px !important; }
          .feature-benefits-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .feature-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 32px !important; }
        }
        @media (max-width: 768px) {
          .feature-hero-btns { flex-direction: column !important; align-items: stretch !important; width: 100%; }
          .feature-hero-btns button, .feature-hero-btns a { width: 100% !important; box-sizing: border-box !important; }
        }
        @media (max-width: 560px) {
          .feature-benefits-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}